import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import './styles.css';
import { registerPmtilesProtocol, buildStyle, wireStyleTextures } from './map/style';
import { createBuildMode, type BuildMode } from './map/build-mode';
import { addKingdomsLayer } from './map/kingdoms-layer';
import { addStructuresLayer } from './map/structures-layer';
import { addTotemsLayer } from './map/totems-layer';
import { createDetailsPanel } from './map/details-panel';
import { wireHover } from './map/hover';
import { collectionBounds, featureBounds, nearestKingdom } from './map/locate';
import { renderStatsPanel } from './hud/stats-panel';
import { mountAccountWidget } from './hud/account-widget';
import type { AccountState } from './hud/account-state';
import { KingdomOverlay } from './overlay/pixi-overlay';
import { api } from './api/client';
import type { KingdomCollection } from './api/models';

// The realm changes while the page is open (kingdoms founded, structures
// built), so re-fetch on an interval instead of showing a load-time snapshot.
const REFRESH_MS = 60_000;

registerPmtilesProtocol();

// The details panel gates its controls on who is signed in, so the account
// widget publishes state changes to it. Build mode is created after the map;
// the panel reaches it lazily through these closures.
let account: AccountState = { kind: 'signed_out' };
let buildMode: BuildMode | undefined;
const detailsEl = document.getElementById('details');
const detailsPanel = detailsEl
  ? createDetailsPanel(detailsEl, () => account, {
      getBuildContext: () => buildMode?.context(),
      resolvePlot: (point) => buildMode?.resolveClick(point) ?? null,
    })
  : null;

// Sign-in unlocks build mode on the player's own kingdom; everything else
// stays read-only. supabase-js loads lazily inside.
const accountEl = document.getElementById('account');
if (accountEl) {
  mountAccountWidget(accountEl, (next) => {
    account = next;
    buildMode?.onAccount(next.kind === 'signed_in');
    // Permissions may have changed while the panel is open.
    detailsPanel?.refresh();
  });
}

const map = new maplibregl.Map({
  container: 'map',
  style: buildStyle(),
  center: [-105.02, 39.61], // Littleton, CO — the dev tile/data area
  zoom: 11,
  // Fixed 2.5D pitch matching the mobile client (cameraPitchDegrees) — the
  // totem extrusions are designed for this read.
  pitch: 50,
  attributionControl: { compact: true },
  // Pan/zoom only, matching mobile: the pitch is part of the game's look and
  // free rotation would break the billboard/totem read.
  dragRotate: false,
  pitchWithRotate: false,
  touchPitch: false,
});
map.touchZoomRotate.disableRotation();
map.keyboard.disableRotation();
wireStyleTextures(map);

buildMode = createBuildMode(map, {
  getToken: async () => (await import('./auth/supabase')).getAccessToken(),
  getPlayer: () => (account.kind === 'signed_in' ? account.player : null),
  onGold: (gold) => {
    if (account.kind === 'signed_in') account.player.gold = gold;
  },
  onBuilt: () => void refresh(),
});
if (detailsPanel) buildMode.attachPanel(detailsPanel);

const overlay = new KingdomOverlay();

let refreshing = false;
let overlayReady = false;

// refresh fetches the public realm data and (re)applies it to every consumer.
// All the sinks are idempotent (setData / innerHTML / marker rebuild), so this
// is the same code path for first paint and every later tick.
async function refresh(): Promise<KingdomCollection | null> {
  if (refreshing) return null;
  refreshing = true;
  try {
    const [kingdoms, structures, stats] = await Promise.all([
      api.publicKingdoms(),
      api.publicStructures(),
      api.publicStats(),
    ]);

    addKingdomsLayer(map, kingdoms);
    // Flat neutral hosts above the kingdom fill; totems render the
    // structures themselves on top as textured 2.5D prisms.
    addStructuresLayer(map, structures);
    await addTotemsLayer(map, structures);

    const statsEl = document.getElementById('stats');
    if (statsEl) renderStatsPanel(statsEl, stats);

    if (overlayReady) overlay.setKingdoms(kingdoms);
    return kingdoms;
  } catch (err) {
    console.error('failed to load realm status', err);
    return null;
  } finally {
    refreshing = false;
  }
}

// The auto-frame is a first-open convenience only: once the viewer moves the
// map themselves, nothing may ever steal the camera back (the geolocation
// answer can arrive seconds late, after they've started exploring).
let userMovedMap = false;
for (const evt of ['dragstart', 'wheel', 'dblclick'] as const) {
  map.on(evt, () => {
    userMovedMap = true;
  });
}

// frameKingdoms decides the opening view: the kingdom nearest the viewer when
// they share their location, otherwise every kingdom in the realm. A starter
// kingdom is a few hundred meters across — invisible at the citywide default
// zoom — so without this, visitors stare at an apparently empty map. Runs
// once, after the first successful fetch — refresh ticks never move the
// camera.
function frameKingdoms(kingdoms: KingdomCollection): void {
  if (kingdoms.features.length === 0) return;

  const fitAll = () => {
    const b = collectionBounds(kingdoms);
    if (b && !userMovedMap) map.fitBounds(b, { padding: 96, maxZoom: 15 });
  };

  if (!('geolocation' in navigator)) {
    fitAll();
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const k = nearestKingdom(
        kingdoms,
        pos.coords.longitude,
        pos.coords.latitude,
      );
      const b = k && featureBounds(k);
      if (b && !userMovedMap) map.fitBounds(b, { padding: 96, maxZoom: 15 });
      else if (!b) fitAll();
    },
    // Denied or unavailable — frame the whole realm instead.
    () => fitAll(),
    { maximumAge: 60_000, timeout: 5_000 },
  );
}

map.on('load', async () => {
  // Click details panel: structures (totem or host) show type/builder/level;
  // anywhere else in a kingdom shows the kingdom's stats — the web mirror of
  // the mobile tap sheets. Hovering a structure or a kingdom's name label
  // shows the same details as a tooltip glance.
  detailsPanel?.wire(map);
  wireHover(map);

  // The Pixi overlay is decorative (pulsing markers) and its WebGL init can
  // fail — e.g. browser context limits after rapid reloads (each load holds
  // two contexts: MapLibre + Pixi). It must never gate the realm data.
  const mapEl = document.getElementById('map');
  if (mapEl) {
    try {
      await overlay.mount(map, mapEl);
      overlayReady = true;
    } catch (err) {
      console.error('overlay disabled (mount failed)', err);
    }
  }

  let kingdoms = await refresh();
  if (!kingdoms) {
    // A transient failure on first paint would otherwise leave the panel
    // empty for a whole refresh interval — retry once, quickly.
    await new Promise((r) => setTimeout(r, 5_000));
    kingdoms = await refresh();
  }
  if (kingdoms) frameKingdoms(kingdoms);

  setInterval(() => {
    // Background tabs skip the fetch; the next visible tick catches up.
    if (!document.hidden) void refresh();
  }, REFRESH_MS);
});
